import { computed } from "vue";
import { type ListItem } from "@airpr/360-ui-components";
import { useChartsStore } from "~/store/charts.ts";
import { ChartTemplate } from "~/types/ChartsType";

export function useChartTemplate() {
  const chartsStore = useChartsStore();

  const shouldSaveTemplate = computed(
    () =>
      chartsStore.selectedChartTemplate.attributes.organization_id === null &&
      chartsStore.selectedChartTemplate.attributes.search_dashboard_id === null,
  );

  const saveTemplate = async (
    selectedTemplate: ListItem,
    chartArray = null,
  ) => {
    const isUserDefaultTemplate =
      Number(selectedTemplate.id) <= 2 || !selectedTemplate.name;
    const { template } = await chartsStore.saveTemplate(
      selectedTemplate,
      isUserDefaultTemplate,
      chartArray,
    );
    // After saving, the charts are fetched and stored in analyticsCharts
    const charts = chartsStore.analyticsCharts.get(template.id);
    return charts;
  };

  const saveCustomTemplate = async (
    selectedTemplate: ListItem | undefined,
    callback: Function,
  ) => {
    let errorMessage: string | null = null;
    try {
      if (selectedTemplate) {
        await chartsStore.saveTemplate(selectedTemplate, false);
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        // eslint-disable-next-line no-console
        console.error("Caught an error:", error.message);
        errorMessage = error.message;
      } else {
        // eslint-disable-next-line no-console
        console.error("Caught an unexpected error:", error);
        errorMessage = "notifications.error";
      }
    } finally {
      if (callback) callback(errorMessage);
    }
  };

  const applyCustomTemplate = async (
    selectedTemplate: ListItem | undefined,
    callback: Function,
  ) => {
    let errorMessage: string | null = null;

    try {
      if (selectedTemplate) {
        await chartsStore.applyTemplate(selectedTemplate);
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        // eslint-disable-next-line no-console
        console.error("Caught an error:", error.message);
        errorMessage = error.message;
      } else {
        // eslint-disable-next-line no-console
        console.error("Caught an unexpected error:", error);
        errorMessage = "notifications.error";
      }
    } finally {
      if (callback) callback(errorMessage);
    }
  };

  const findSelectedTemplate = () =>
    chartsStore.chartTemplates
      .map((chartTemplate: ChartTemplate) => ({
        id: chartTemplate.id,
        name: chartTemplate.attributes.name,
      }))
      .find(
        (template: ListItem) =>
          Number(template.id) === Number(chartsStore.selectedChartTemplate.id),
      );

  return {
    shouldSaveTemplate,
    saveTemplate,
    saveCustomTemplate,
    applyCustomTemplate,
    findSelectedTemplate,
  };
}
